import { useMemo } from 'react';
import { useContainersByUris } from 'src/hooks/services/useContainer';
import { useLotsByUris } from 'src/hooks/services/useLot';
import { useMaterialsByUri } from 'src/hooks/services/useMaterials';
import { API_RESOURCES } from 'src/utils/constants';
import { getResourceUri, getUuid } from 'src/utils/url';

/**
 * Resolves material lot names and container metadata keyed by container UUID.
 *
 * Pass already-loaded `containers` when available to avoid re-fetching.
 */
const useMaterialLotNamesByContainers = ({ containerUuids = [], containers = null } = {}) => {
  const shouldFetchContainers = !containers && containerUuids.length > 0;

  const containerUris = useMemo(
    () =>
      containerUuids
        .filter(Boolean)
        .map(uuid => getResourceUri(API_RESOURCES.MATERIAL_CONTAINER, uuid)),
    [containerUuids]
  );

  const { data: containersByUri, isLoading: isContainersLoading } = useContainersByUris(
    shouldFetchContainers ? containerUris : [],
    'material-lot-names-by-containers'
  );

  const resolvedContainers = useMemo(() => {
    if (containers) {
      return containers;
    }
    return Object.values(containersByUri ?? {});
  }, [containers, containersByUri]);

  const materialLotUris = useMemo(
    () => [...new Set(resolvedContainers.map(container => container.material_lot).filter(Boolean))],
    [resolvedContainers]
  );

  const materialUris = useMemo(
    () => [...new Set(resolvedContainers.map(container => container.material).filter(Boolean))],
    [resolvedContainers]
  );

  const { data: materialLots = [], isLoading: isLotsLoading } = useLotsByUris(
    materialLotUris,
    'material-lot-names'
  );

  const { data: materials = [], isLoading: isMaterialsLoading } = useMaterialsByUri(
    materialUris,
    'scanned-container-materials'
  );

  const namesByUuid = {};
  const metaByUuid = {};

  containerUuids.forEach(uuid => {
    const container = resolvedContainers.find(item => getUuid(item.uri) === uuid);
    const lot = materialLots.find(item => item.uri === container?.material_lot);
    const material = materials.find(item => item.uri === container?.material);

    if (lot?.name) {
      namesByUuid[uuid] = lot.name;
    }

    if (container) {
      metaByUuid[uuid] = {
        quantity: container.quantity,
        units: material?.units || lot?.units,
        color: material?.color,
        materialName: material?.name || lot?.material_name,
      };
    }
  });

  return {
    namesByUuid,
    metaByUuid,
    isLoading:
      (shouldFetchContainers && isContainersLoading) ||
      (materialLotUris.length > 0 && isLotsLoading) ||
      (materialUris.length > 0 && isMaterialsLoading),
  };
};

export default useMaterialLotNamesByContainers;
